import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

/**
 * EditTenantDialog
 * Modal for fixing typos from the Stripe checkout flow (business name and
 * owner email) and triggering email-side admin actions:
 *   - Resend welcome email
 *   - Send password-setup link
 *
 * Extracted from pages/Admin.jsx during the production-readiness pass.
 * No behavioural change.
 */
export default function EditTenantDialog({ tenant, onClose, onSaved }) {
    const [email, setEmail] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [saving, setSaving] = useState(false);
    const [resending, setResending] = useState(false);
    const [sendingSetup, setSendingSetup] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (tenant) {
            setEmail(tenant.owner_email || "");
            setBusinessName(tenant.business_name || "");
            setError("");
        }
    }, [tenant]);

    const cleanEmail = email.trim().toLowerCase();
    const cleanName = businessName.trim();
    const emailChanged = tenant && cleanEmail !== (tenant.owner_email || "").toLowerCase();
    const nameChanged = tenant && cleanName !== (tenant.business_name || "");
    const canSubmit = (emailChanged || nameChanged)
        && cleanName.length >= 2
        && /^\S+@\S+\.\S+$/.test(cleanEmail);

    const save = async () => {
        if (!tenant) return;
        setError("");
        setSaving(true);
        try {
            const body = {};
            if (emailChanged) body.email = cleanEmail;
            if (nameChanged) body.business_name = cleanName;
            await api.patch(`/admin/tenants/${tenant.id}`, body);
            toast.success("Tenant updated");
            await onSaved();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const resendWelcome = async () => {
        if (!tenant) return;
        setResending(true);
        try {
            const { data } = await api.post(`/admin/tenants/${tenant.id}/resend-welcome`);
            if (data?.sent) {
                toast.success(`Welcome email re-sent to ${data.to}`);
            } else {
                toast(`Email queued (mail provider not configured for live send)`);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to resend");
        } finally {
            setResending(false);
        }
    };

    const sendSetupLink = async () => {
        if (!tenant) return;
        if (
            !window.confirm(
                "Send a one-click password setup link to this tenant? Their existing password will keep working until they use the new link."
            )
        )
            return;
        setSendingSetup(true);
        try {
            const { data } = await api.post(`/admin/tenants/${tenant.id}/send-setup-link`);
            if (data?.sent) {
                toast.success(`Setup link sent to ${data.to}`);
            } else {
                toast(`Setup link queued (mail provider not configured for live send)`);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send setup link");
        } finally {
            setSendingSetup(false);
        }
    };

    return (
        <AnimatePresence>
            {tenant && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
                    onClick={onClose}
                    data-testid="edit-tenant-overlay"
                >
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:max-w-[460px] bg-[#0a0a0a] border border-zinc-800 rounded-t-2xl sm:rounded-xl p-6"
                        data-testid="edit-tenant-dialog"
                    >
                        <div className="flex items-start justify-between">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Edit tenant</p>
                                <h2 className="mt-1 text-xl font-medium text-zinc-50 tracking-tight truncate">
                                    {tenant.business_name}
                                </h2>
                                <p className="mt-0.5 text-[12.5px] text-zinc-500">
                                    Fix typos from checkout. Login email + business name only.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="edit-tenant-close"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="mt-6 space-y-4">
                            <label className="block">
                                <span className="block text-[12.5px] text-zinc-400 font-medium mb-2">Business name</span>
                                <input
                                    type="text"
                                    maxLength={120}
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                    data-testid="edit-tenant-business-name"
                                    className="w-full h-11 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 outline-none transition-colors focus:border-zinc-500"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[12.5px] text-zinc-400 font-medium mb-2">Owner email</span>
                                <input
                                    type="email"
                                    maxLength={120}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="edit-tenant-email"
                                    className="w-full h-11 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 outline-none transition-colors focus:border-zinc-500"
                                />
                                <p className="mt-1.5 text-[11.5px] text-zinc-600">
                                    Updating the email also changes the client's login.
                                </p>
                            </label>
                            {error && (
                                <p className="text-[12.5px] text-red-400" data-testid="edit-tenant-error">{error}</p>
                            )}
                        </div>

                        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={resendWelcome}
                                    disabled={resending}
                                    data-testid="edit-tenant-resend-welcome"
                                    className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-zinc-800 text-[13px] text-zinc-300 hover:bg-zinc-900 transition-colors disabled:opacity-50"
                                >
                                    {resending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Mail className="h-4 w-4" />
                                    )}
                                    Resend welcome
                                </button>
                                <button
                                    type="button"
                                    onClick={sendSetupLink}
                                    disabled={sendingSetup}
                                    data-testid="edit-tenant-send-setup-link"
                                    className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-zinc-800 text-[13px] text-zinc-300 hover:bg-zinc-900 transition-colors disabled:opacity-50"
                                >
                                    {sendingSetup ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <KeyRound className="h-4 w-4" />
                                    )}
                                    Send setup link
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    data-testid="edit-tenant-cancel"
                                    className="h-10 px-4 rounded-md border border-zinc-800 text-[13px] text-zinc-300 hover:bg-zinc-900 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={!canSubmit || saving}
                                    data-testid="edit-tenant-save"
                                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Save changes
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
